<template>
  <div>
    <v-row class="mt-0 revendedores-layout">
      <!-- ================================================================ -->
      <!-- COLUMNA IZQUIERDA: Gestión de Solicitudes                         -->
      <!-- ================================================================ -->
      <v-col cols="12" :lg="puedeGestionarAbm ? 5 : 12" class="revendedores-col revendedores-col--left">
        <div class="d-flex align-center justify-space-between mb-2 revendedores-section-header">
          <div class="d-flex align-center">
            <span class="text-h5">Solicitudes de atención</span>
            <v-chip
              v-if="pendingCount > 0"
              x-small
              color="warning"
              text-color="white"
              class="ml-2"
            >
              {{ pendingCount }} pendiente{{ pendingCount !== 1 ? 's' : '' }}
            </v-chip>
          </div>
          <v-btn
            small
            text
            color="primary"
            :loading="loadingSolicitudes"
            @click="recargarSolicitudes"
          >
            <v-icon small left>fas fa-sync</v-icon>
            Actualizar
          </v-btn>
        </div>

        <v-card outlined class="mb-3">
          <v-card-text class="pb-2">
            <v-row dense>
              <v-col cols="12" md="4">
                <v-autocomplete
                  v-model="filtrosSolicitudes.estado"
                  :items="estadosSolicitud"
                  label="Estado"
                  hide-details
                  outlined
                  dense
                  clearable
                ></v-autocomplete>
              </v-col>
              <v-col cols="12" md="4">
                <v-autocomplete
                  v-model="filtrosSolicitudes.revendedor_id"
                  :items="revendedoresOptions"
                  item-text="nombre"
                  item-value="id"
                  label="Revendedor"
                  hide-details
                  outlined
                  dense
                  clearable
                  :loading="loadingRevendedoresOptions"
                  no-data-text="No hay revendedores disponibles"
                ></v-autocomplete>
              </v-col>
              <v-col cols="12" md="4">
                <v-autocomplete
                  v-model="filtrosSolicitudes.asesor_asignado_id"
                  :items="usuariosSolicitudesOptions"
                  item-text="label"
                  item-value="id"
                  label="Usuario"
                  hide-details
                  outlined
                  dense
                  clearable
                  :loading="loadingUsuariosSolicitudes"
                  no-data-text="No hay usuarios disponibles"
                ></v-autocomplete>
              </v-col>
              <v-col cols="12" class="d-flex flex-wrap" style="gap: 8px;">
                <v-btn
                  small
                  color="primary"
                  :loading="loadingSolicitudes"
                  @click="recargarSolicitudes"
                >
                  <v-icon left small>fas fa-filter</v-icon>
                  Filtrar
                </v-btn>
                <v-btn
                  small
                  text
                  color="secondary"
                  @click="limpiarFiltrosSolicitudes"
                >
                  Limpiar
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-card outlined>
          <!-- Lista de solicitudes -->
          <v-list
            v-if="solicitudesOrdenadas.length > 0"
            dense
            max-height="280px"
            class="overflow-y-auto"
          >
            <v-list-item-group
              v-model="solicitudSeleccionadaIndex"
              color="primary"
              mandatory
            >
              <v-list-item
                v-for="(sol, i) in solicitudesOrdenadas"
                :key="sol.id"
                :class="['revendedores-solicitud', filaSolicitudClass(sol)]"
                @click="seleccionarSolicitud(sol, i)"
                @contextmenu="clickDerecho($event, sol)"
              >
                <v-list-item-content>
                  <v-list-item-title class="text-body-2 font-weight-medium">
                    {{ sol.revendedor_nombre }}
                    <v-chip
                      x-small
                      :color="colorEstado(sol.estado)"
                      text-color="white"
                      class="ml-1"
                    >
                      {{ sol.estado }}
                    </v-chip>
                  </v-list-item-title>
                  <v-list-item-subtitle class="text-caption">
                    {{ sol.mensaje ? truncar(sol.mensaje, 60) : 'Sin mensaje' }}
                  </v-list-item-subtitle>
                  <v-list-item-subtitle class="text-caption revendedores-muted-text">
                    {{ sol.asesor_username ? 'Asesor: ' + sol.asesor_username : 'Sin asignar' }}
                    &mdash; {{ fechaRelativa(sol.fecha_creacion) }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list-item-group>
          </v-list>

          <!-- Estado vacío -->
          <v-card-text v-else class="text-center py-6">
            <v-icon size="48" color="disabled">fas fa-inbox</v-icon>
            <p class="text-body-2 revendedores-muted-text mt-2 mb-0">
              {{ loadingSolicitudes ? 'Cargando solicitudes...' : 'No hay solicitudes para los filtros actuales' }}
            </p>
          </v-card-text>
        </v-card>

        <!-- Menú contextual con click derecho -->
        <v-menu v-model="mostrarMenu" :position-x="x" :position-y="y" absolute offset-y>
          <v-list dense>
            <v-list-item
              v-if="solicitudMenuContextual && puedeTomarSolicitudPara(solicitudMenuContextual)"
              @click="tomarSolicitudDesdeMenu"
            >
              <v-list-item-icon class="mr-2">
                <v-icon small color="primary">fas fa-hand-paper</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="text-body-2">Tomar solicitud</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item
              v-if="solicitudMenuContextual && puedeReasignarSolicitudPara(solicitudMenuContextual)"
              @click="reasignarSolicitudDesdeMenu"
            >
              <v-list-item-icon class="mr-2">
                <v-icon small color="warning">fas fa-random</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="text-body-2">Reasignar</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item
              v-if="solicitudMenuContextual && puedeFinalizarSolicitudPara(solicitudMenuContextual)"
              @click="finalizarSolicitudDesdeMenu"
            >
              <v-list-item-icon class="mr-2">
                <v-icon small color="success">fas fa-check</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="text-body-2">Finalizar</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item
              v-if="solicitudMenuContextual && solicitudMenuContextual.telefono"
              @click="contactarWhatsApp"
            >
              <v-list-item-icon class="mr-2">
                <v-icon small color="wpp">fab fa-whatsapp</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="text-body-2">Contactar</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-menu>

        <!-- Detalle de solicitud seleccionada -->
        <v-card
          v-if="solicitudSeleccionada"
          outlined
          class="mt-4"
          style="position: relative"
        >
          <v-btn
            icon
            x-small
            style="position: absolute; top: 4px; right: 4px;"
            @click="cerrarDetalle"
          >
            <v-icon>fas fa-times</v-icon>
          </v-btn>
          <v-card-text>
            <div class="d-flex align-center justify-space-between mb-2 mt-2">
              <span class="text-subtitle-1 font-weight-bold">
                {{ solicitudSeleccionada.revendedor_nombre }}
              </span>
              <v-chip
                small
                :color="colorEstado(solicitudSeleccionada.estado)"
                text-color="white"
              >
                {{ solicitudSeleccionada.estado }}
              </v-chip>
            </div>

            <div class="text-body-2 mb-1">
              <strong>Teléfono:</strong>
              {{ solicitudSeleccionada.telefono || '—' }}
            </div>

            <div class="text-body-2 mb-1">
              <strong>Contacto:</strong>
              {{ solicitudSeleccionada.nombre_contacto || '—' }}
            </div>

            <div v-if="solicitudSeleccionada.numero_preventa" class="text-body-2 mb-1">
              <strong>Preventa:</strong>
              {{ solicitudSeleccionada.numero_preventa }}
            </div>

            <div class="text-body-2 mb-3">
              <strong>Mensaje:</strong>
              <span class="d-block mt-1">{{ solicitudSeleccionada.mensaje || 'Sin mensaje' }}</span>
            </div>

            <div v-if="solicitudSeleccionada.asesor_username" class="text-body-2 mb-2 revendedores-muted-text">
              Asignado a: <strong>{{ solicitudSeleccionada.asesor_username }}</strong>
            </div>

            <div class="text-caption revendedores-muted-text mb-3">
              {{ solicitudSeleccionada.fecha_creacion ? 'Creado: ' + formatearFechaHora(solicitudSeleccionada.fecha_creacion) : '' }}
              <span v-if="solicitudSeleccionada.fecha_asignacion">
                &mdash; Asignado: {{ formatearFechaHora(solicitudSeleccionada.fecha_asignacion) }}
              </span>
            </div>

            <!-- Acciones -->
            <div class="d-flex flex-wrap" style="gap: 8px;">
              <!-- Botón WhatsApp -->
              <v-btn
                v-if="solicitudSeleccionada.telefono"
                color="wpp"
                dark
                small
                :href="linkWhatsApp"
                target="_blank"
              >
                <v-icon left small>fab fa-whatsapp</v-icon>
                WhatsApp
              </v-btn>

              <!-- Botón Tomar solicitud -->
              <v-btn
                v-if="puedeTomarSolicitud"
                color="primary"
                small
                :loading="tomandoSolicitud"
                @click="tomarSolicitudActual"
              >
                <v-icon left small>fas fa-hand-paper</v-icon>
                Tomar solicitud
              </v-btn>

              <!-- Botón Reasignar -->
              <v-btn
                v-if="puedeReasignarSolicitud"
                color="warning"
                small
                @click="abrirDialogReasignar"
              >
                <v-icon left small>fas fa-random</v-icon>
                Reasignar
              </v-btn>

              <v-btn
                v-if="puedeFinalizarSolicitud"
                color="success"
                small
                :loading="finalizandoSolicitud"
                @click="dialogFinalizar = true"
              >
                <v-icon left small>fas fa-check</v-icon>
                Finalizar
              </v-btn>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col v-if="puedeGestionarAbm" cols="12" class="py-0 d-lg-none">
        <v-divider class="my-2 revendedores-mobile-divider"></v-divider>
      </v-col>

      <!-- ================================================================ -->
      <!-- COLUMNA DERECHA: ABM de Revendedores                              -->
      <!-- ================================================================ -->
      <v-col v-if="puedeGestionarAbm" cols="12" lg="7" class="revendedores-col revendedores-col--right">
        <div class="d-flex align-center justify-space-between mb-2 revendedores-section-header">
          <span class="text-h5">Revendedores</span>
          <div>
            <v-btn
              class="mr-2"
              color="info"
              small
              depressed
              title="Verificar que los nÃºmeros sigan activos en BuilderBot"
              :loading="verificandoBuilderbot"
              @click="verificarBuilderbot"
            >
              <v-icon left x-small>fas fa-shield-alt</v-icon>
              Verificar Bot
            </v-btn>
            <v-btn
              class="revendedores-add-btn"
              color="success"
              small
              fab
              title="Nuevo Revendedor"
              @click="abrirDialogNuevo"
            >
              <v-icon>fas fa-plus</v-icon>
            </v-btn>
          </div>
        </div>

        <!-- Filtros inline -->
        <v-form @submit.prevent="buscar">
          <v-row :no-gutters="$vuetify.breakpoint.xs">
            <v-col cols="12" sm="3" class="py-1 pr-sm-2">
              <v-text-field
                v-model.trim="search.nombre"
                label="Nombre"
                hide-details
                outlined
                dense
                clearable
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="3" class="py-1 pr-sm-2">
              <v-text-field
                v-model.trim="search.telefono"
                label="Teléfono"
                hide-details
                outlined
                dense
                clearable
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="3" class="py-1 pr-sm-2">
              <v-text-field
                v-model.trim="search.email"
                label="Email"
                hide-details
                outlined
                dense
                clearable
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="3" class="py-1 d-flex align-center">
              <v-btn
                color="primary"
                type="submit"
                :loading="loading"
                depressed
              >
                <v-icon left small>fas fa-search</v-icon>
                Buscar
              </v-btn>
            </v-col>
          </v-row>
        </v-form>

        <v-card class="mt-6">
          <div class="revendedores-table-wrap">
            <v-data-table
              class="cebra elevation-0 mt-2"
              item-key="id"
              :headers="headers"
              :items="revendedores"
              :loading="loading"
              dense
            >
            <template v-slot:[`item.telefono_canonico`]="{ item }">
              {{ formatearTelefono(item.telefono_canonico) }}
            </template>

            <template v-slot:[`item.revendedor_sai_id`]="{ item }">
              {{ item.revendedor_sai_nombre || obtenerNombreVendedorSai(item.revendedor_sai_id) }}
            </template>

            <template v-slot:[`item.builderbot_whitelisted`]="{ item }">
              <v-chip
                v-if="item.builderbot_whitelisted == 1"
                small
                 color="success"
                text-color="white"
              >
                Activo
              </v-chip>
              <v-chip
                v-else-if="item.builderbot_error"
                small
                 color="error"
                text-color="white"
              >
                {{ item.builderbot_error }}
              </v-chip>
              <v-chip
                v-else
                small
                 color="warning"
                text-color="white"
              >
                Pendiente
              </v-chip>
            </template>

            <template v-slot:[`item.acciones`]="{ item }">
              <v-btn
                small
                icon
                color="warning"
                title="Editar"
                @click="abrirDialogEditar(item)"
              >
                <v-icon small>fas fa-edit</v-icon>
              </v-btn>
              <v-btn
                small
                icon
                color="error"
                title="Inhabilitar"
                @click="inhabilitar(item)"
              >
                <v-icon small>fas fa-trash</v-icon>
              </v-btn>
              <v-btn
                v-if="item.builderbot_whitelisted != 1"
                small
                icon
                color="info"
                title="Reintentar sincronización"
                @click="sincronizarBuilderbot(item.id)"
              >
                <v-icon small>fas fa-sync</v-icon>
              </v-btn>
            </template>
            </v-data-table>
          </div>
        </v-card>

        <!-- Dialog Crear/Editar -->
        <v-dialog v-model="dialog" max-width="600px" persistent>
          <v-card>
            <v-card-title class="text-h5">
              {{ dialogTitle }}
            </v-card-title>
            <v-card-text>
              <v-form ref="form" @submit.prevent="guardar">
                <v-autocomplete
                  v-model="form.revendedor_sai_id"
                  :items="saiVendedores"
                  item-text="Nombre"
                  item-value="VendedorID"
                  label="Revendedores AutoDealer"
                  :rules="[rules.required]"
                  :hint="hintVendedorSai"
                  persistent-hint
                  outlined
                  dense
                  class="mt-4"
                  clearable
                  no-data-text="No se encontraron revendedores AutoDealer disponibles"
                ></v-autocomplete>
                <v-text-field
                  v-model.trim="form.telefono"
                  label="Teléfono"
                  :rules="[rules.required]"
                  persistent-hint
                  outlined
                  dense
                ></v-text-field>
                <v-text-field
                  v-model.trim="form.email"
                  label="Email"
                  :rules="[rules.email]"
                  outlined
                  dense
                ></v-text-field>
                <v-textarea
                  v-model.trim="form.comentario"
                  label="Comentario"
                  rows="3"
                  auto-grow
                  outlined
                  dense
                  hint="Comentario interno sobre el revendedor"
                  persistent-hint
                ></v-textarea>
              </v-form>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn text @click="dialog = false">Cancelar</v-btn>
              <v-btn
                color="primary"
                :loading="guardando"
                @click="guardar"
              >
                Guardar
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- Dialog Confirmación Inhabilitar -->
        <v-dialog v-model="dialogInhabilitar" max-width="450px">
          <v-card>
            <v-card-title class="text-h5">
              Confirmar inhabilitación
            </v-card-title>
            <v-card-text>
              ¿Está seguro que desea inhabilitar a
              <strong>{{ revendedorAInhabilitar ? revendedorAInhabilitar.nombre : '' }}</strong>?
              Será eliminado de las respuestas del bot con Inteligencia Artificial.
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn text @click="dialogInhabilitar = false">Cancelar</v-btn>
              <v-btn
                color="error"
                :loading="inhabilitando"
                @click="confirmarInhabilitar"
              >
                Inhabilitar
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-dialog v-model="dialogReasignar" max-width="500px">
          <v-card>
            <v-card-title class="text-h5">
              Reasignar solicitud
            </v-card-title>
            <v-card-text>
              <div class="text-body-2 mb-4">
                Seleccione el asesor que recibirá la solicitud de
                <strong>{{ solicitudSeleccionada ? solicitudSeleccionada.revendedor_nombre : '' }}</strong>.
              </div>

              <v-autocomplete
                v-model="reasignarForm.asesor_asignado_id"
                :items="usuariosSolicitudesOptions"
                item-text="label"
                item-value="id"
                label="Nuevo asesor"
                outlined
                dense
                clearable
                :loading="loadingUsuariosSolicitudes"
                no-data-text="No hay usuarios disponibles"
              ></v-autocomplete>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn text @click="dialogReasignar = false">Cancelar</v-btn>
              <v-btn
                color="warning"
                :loading="reasignandoSolicitud"
                @click="confirmarReasignacion"
              >
                Reasignar
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- Dialog Confirmación Finalizar -->
        <v-dialog v-model="dialogFinalizar" max-width="450px">
          <v-card>
            <v-card-title class="text-h5">
              Confirmar finalización
            </v-card-title>
            <v-card-text>
              ¿Está seguro que desea finalizar la solicitud de
              <strong>{{ solicitudSeleccionada ? solicitudSeleccionada.revendedor_nombre : '' }}</strong>?
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn text @click="dialogFinalizar = false">Cancelar</v-btn>
              <v-btn
                color="success"
                :loading="finalizandoSolicitud"
                @click="confirmarFinalizar"
              >
                Finalizar
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import moment from 'moment'
import { mapState } from 'vuex'

export default {
  data() {
    return {
      search: {
        nombre: '',
        telefono: '',
        email: ''
      },

      dialog: false,
      dialogTitle: '',
      guardando: false,
      verificandoBuilderbot: false,

      dialogInhabilitar: false,
      revendedorAInhabilitar: null,
      inhabilitando: false,

      form: {
        id: null,
        telefono: '',
        email: '',
        revendedor_sai_id: null,
        comentario: ''
      },

      headers: [
        { text: 'Nombre', value: 'nombre' },
        { text: 'Teléfono', value: 'telefono_canonico' },
        { text: 'Email', value: 'email' },
        { text: 'Comentario', value: 'comentario', sortable: false },
        // { text: 'Revendedor AutoDealer', value: 'revendedor_sai_id', sortable: false },
        { text: 'Bot de IA', value: 'builderbot_whitelisted', sortable: false },
        { text: 'Acciones', value: 'acciones', sortable: false }
      ],

      rules: {
        required: value => !!value || 'Campo requerido',
        email: value => {
          if (!value) return true
          const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
          return pattern.test(value) || 'Email inválido'
        }
      },

      // Solicitudes
      filtrosSolicitudes: {
        estado: null,
        revendedor_id: null,
        asesor_asignado_id: null
      },
      estadosSolicitud: ['PENDIENTE', 'ASIGNADO', 'REASIGNADO', 'RESUELTO'],
      solicitudSeleccionadaIndex: -1,
      tomandoSolicitud: false,
      finalizandoSolicitud: false,
      dialogReasignar: false,
      reasignandoSolicitud: false,
      dialogFinalizar: false,
      loadingUsuariosSolicitudes: false,
      usuariosAsignablesSolicitudes: [],
      revendedoresOptions: [],
      loadingRevendedoresOptions: false,
      pollingInterval: null,
      reasignarForm: {
        asesor_asignado_id: null
      },
      mostrarMenu: false,
      x: 0,
      y: 0,
      solicitudMenuContextual: null
    }
  },

  computed: {
    ...mapState('revendedores', [
      'revendedores',
      'saiVendedores',
      'solicitudes',
      'solicitudSeleccionada',
      'permisos',
      'loading',
      'loadingSolicitudes'
    ]),

    puedeGestionarAbm() {
      return this.permisos && this.permisos.abm_gestion === true
    },

    hintVendedorSai() {
      return this.saiVendedores.length === 0
        ? 'No se encontraron revendedores disponibles en AutoDealer. Délo de alta en Bianchi → Tablas → Vendedores con el nombre "RV "'
        : ''
    },

    puedeTomarSolicitud() {
      if (!this.solicitudSeleccionada) return false
      return this.solicitudSeleccionada.estado === 'PENDIENTE' ||
             (['ASIGNADO', 'REASIGNADO'].includes(this.solicitudSeleccionada.estado) && !this.solicitudSeleccionada.asesor_asignado_id)
    },

    puedeReasignarSolicitud() {
      if (!this.solicitudSeleccionada) return false
      return this.permisos &&
             this.permisos.consultas_reasignar === true &&
             this.solicitudSeleccionada.estado !== 'PENDIENTE' &&
             this.solicitudSeleccionada.estado !== 'RESUELTO' &&
             !!this.solicitudSeleccionada.asesor_asignado_id
    },

    puedeFinalizarSolicitud() {
      if (!this.solicitudSeleccionada) return false
      return ['ASIGNADO', 'REASIGNADO'].includes(this.solicitudSeleccionada.estado)
    },

    usuariosSolicitudesOptions() {
      const asesorActualId = this.solicitudSeleccionada
        ? this.solicitudSeleccionada.asesor_asignado_id
        : null
      return (this.usuariosAsignablesSolicitudes || [])
        .filter(usuario => usuario.id !== asesorActualId)
        .map(usuario => {
          const nombre = [usuario.nombre, usuario.apellido].filter(Boolean).join(' ').trim()
          return {
            id: usuario.id,
            label: nombre ? `${nombre} (${usuario.username})` : usuario.username
          }
        })
    },

    solicitudesOrdenadas() {
      const ordenEstados = {
        'PENDIENTE': 1,
        'ASIGNADO': 2,
        'REASIGNADO': 2,
        'RESUELTO': 3
      }
      return [...this.solicitudes].sort((a, b) => {
        const ordenA = ordenEstados[a.estado] || 99
        const ordenB = ordenEstados[b.estado] || 99
        return ordenA - ordenB
      })
    },

    pendingCount() {
      return this.solicitudes.filter(s => s.estado === 'PENDIENTE').length
    },

    linkWhatsApp() {
      if (!this.solicitudSeleccionada || !this.solicitudSeleccionada.telefono) return '#'
      const tel = String(this.solicitudSeleccionada.telefono).replace(/^549/, '')
      const nombre = this.solicitudSeleccionada.revendedor_nombre || 'Revendedor'
      const msg = encodeURIComponent(`Hola ${nombre}, soy asesor de Dycar. Vi tu solicitud y quería comunicarme con vos.`)
      return `https://wa.me/549${tel}?text=${msg}`
    }
  },

  methods: {
    // ── Revendedores ABM ──────────────────────────────────────────

    buscar() {
      if (!this.puedeGestionarAbm) return
      this.$store.dispatch('revendedores/fetch_revendedores', {
        nombre: this.search.nombre,
        telefono: this.search.telefono,
        email: this.search.email,
        inhabilitado: 0
      })
    },

    abrirDialogNuevo() {
      this.dialogTitle = 'Nuevo Revendedor'
      this.form = {
        id: null,
        telefono: '',
        email: '',
        revendedor_sai_id: null,
        comentario: ''
      }
      this.$store.dispatch('revendedores/fetch_sai_vendedores')
      this.dialog = true
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.resetValidation()
        }
      })
    },

    abrirDialogEditar(item) {
      this.dialogTitle = 'Editar Revendedor'
      this.form = {
        id: item.id,
        telefono: item.telefono_canonico,
        email: item.email || '',
        revendedor_sai_id: item.revendedor_sai_id || null,
        comentario: item.comentario || ''
      }
      this.$store.dispatch('revendedores/fetch_sai_vendedores', item.id)
      this.dialog = true
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.resetValidation()
        }
      })
    },

    async guardar() {
      if (!this.$refs.form.validate()) return

      this.guardando = true
      const payload = {
        telefono: this.form.telefono,
        email: this.form.email || null,
        revendedor_sai_id: this.form.revendedor_sai_id,
        comentario: this.form.comentario || null
      }

      let success = false
      if (this.form.id) {
        success = await this.$store.dispatch('revendedores/update_revendedor', {
          id: this.form.id,
          payload
        })
      } else {
        success = await this.$store.dispatch('revendedores/create_revendedor', payload)
      }

      this.guardando = false

      if (success) {
        this.$store.dispatch('show_snackbar', {
          text: this.form.id
            ? 'Revendedor actualizado correctamente'
            : 'Revendedor creado correctamente',
          color: 'success'
        })
        this.dialog = false
        this.buscar()
      }
    },

    inhabilitar(item) {
      this.revendedorAInhabilitar = item
      this.dialogInhabilitar = true
    },

    async confirmarInhabilitar() {
      this.inhabilitando = true
      const id = this.revendedorAInhabilitar.id
      const success = await this.$store.dispatch('revendedores/inhabilitar_revendedor', id)
      this.inhabilitando = false

      if (success) {
        this.dialogInhabilitar = false
        this.$store.dispatch('show_snackbar', {
          text: 'Revendedor inhabilitado correctamente',
          color: 'success'
        })
      }
    },

    sincronizarBuilderbot(id) {
      this.$store.dispatch('revendedores/sincronizar_builderbot', id).then(() => {
        this.buscar()
      })
    },

    verificarBuilderbot() {
      this.verificandoBuilderbot = true
      this.$store.dispatch('revendedores/verificar_builderbot').then((data) => {
        this.verificandoBuilderbot = false
        this.buscar()
        if (data && data.data && data.data.desincronizados > 0) {
          this.$store.dispatch('show_snackbar', {
            text: `Se encontraron ${data.data.desincronizados} revendedor(es) desincronizados. Use el botÃ³n de sincronizaciÃ³n en cada uno.`,
            color: 'warning'
          })
        }
      }).catch(() => {
        this.verificandoBuilderbot = false
      })
    },

    obtenerNombreVendedorSai(revendedor_sai_id) {
      const revendedor = this.revendedores.find(item => item.revendedor_sai_id === revendedor_sai_id)
      if (revendedor && revendedor.revendedor_sai_nombre) return revendedor.revendedor_sai_nombre

      if (!revendedor_sai_id || !this.saiVendedores.length) return '—'

      const vendedor = this.saiVendedores.find(v => v.VendedorID === revendedor_sai_id)
      return vendedor ? vendedor.Nombre : `ID ${revendedor_sai_id}`
    },

    formatearTelefono(telefono) {
      if (!telefono) return ''
      const str = String(telefono)
      if (str.length >= 10) {
        return str.replace(/(\d{2,4})(\d{3,4})(\d{4})/, '$1 $2 $3')
      }
      return str
    },

    // ── Solicitudes ───────────────────────────────────────────────

    async cargarRevendedores() {
      this.loadingRevendedoresOptions = true
      try {
        const revendedores = await this.$store.dispatch('revendedores/fetch_revendedores_lista')
        this.revendedoresOptions = Array.isArray(revendedores) ? revendedores : []
      } catch (error) {
        this.revendedoresOptions = []
      } finally {
        this.loadingRevendedoresOptions = false
      }
    },

    async cargarUsuariosSolicitudes() {
      this.loadingUsuariosSolicitudes = true
      try {
        const usuarios = await this.$store.dispatch('revendedores/fetch_usuarios_asignables')
        this.usuariosAsignablesSolicitudes = Array.isArray(usuarios) ? usuarios : []
      } catch (error) {
        this.usuariosAsignablesSolicitudes = []
        this.$store.dispatch('show_snackbar', {
          text: error.message,
          color: 'error'
        })
      } finally {
        this.loadingUsuariosSolicitudes = false
      }
    },

    async recargarSolicitudes() {
      const solicitudIdActual = this.solicitudSeleccionada ? this.solicitudSeleccionada.id : null

      await this.$store.dispatch('revendedores/fetch_solicitudes', {
        estado: this.filtrosSolicitudes.estado,
        revendedor_id: this.filtrosSolicitudes.revendedor_id,
        asesor_asignado_id: this.filtrosSolicitudes.asesor_asignado_id
      })

      this.sincronizarSolicitudSeleccionada(solicitudIdActual)
    },

    limpiarFiltrosSolicitudes() {
      this.filtrosSolicitudes = {
        estado: null,
        revendedor_id: null,
        asesor_asignado_id: null
      }
      this.recargarSolicitudes()
    },

    sincronizarSolicitudSeleccionada(solicitudId) {
      if (!solicitudId) {
        if (this.solicitudesOrdenadas.length > 0) {
          this.seleccionarSolicitud(this.solicitudesOrdenadas[0], 0)
        } else {
          this.solicitudSeleccionadaIndex = -1
          this.$store.commit('revendedores/set_solicitud_seleccionada', null)
        }
        return
      }

      const index = this.solicitudesOrdenadas.findIndex(s => s.id === solicitudId)
      if (index !== -1) {
        this.seleccionarSolicitud(this.solicitudesOrdenadas[index], index)
      } else if (this.solicitudesOrdenadas.length > 0) {
        this.seleccionarSolicitud(this.solicitudesOrdenadas[0], 0)
      } else {
        this.solicitudSeleccionadaIndex = -1
        this.$store.commit('revendedores/set_solicitud_seleccionada', null)
      }
    },

    seleccionarSolicitud(sol, index) {
      this.solicitudSeleccionadaIndex = index
      this.$store.commit('revendedores/set_solicitud_seleccionada', sol)
    },

    cerrarDetalle() {
      this.solicitudSeleccionadaIndex = -1
      this.$store.commit('revendedores/set_solicitud_seleccionada', null)
    },

    clickDerecho(event, sol) {
      event.preventDefault()
      this.mostrarMenu = false
      this.x = event.clientX
      this.y = event.clientY
      this.solicitudMenuContextual = sol
      this.$nextTick(() => {
        this.mostrarMenu = true
      })
    },

    // ── Helpers para menú contextual en rows ──────────────────────
    puedeTomarSolicitudPara(sol) {
      return sol.estado === 'PENDIENTE' ||
             (['ASIGNADO', 'REASIGNADO'].includes(sol.estado) && !sol.asesor_asignado_id)
    },

    puedeReasignarSolicitudPara(sol) {
      return this.permisos &&
             this.permisos.consultas_reasignar === true &&
             sol.estado !== 'PENDIENTE' &&
             sol.estado !== 'RESUELTO' &&
             !!sol.asesor_asignado_id
    },

    puedeFinalizarSolicitudPara(sol) {
      return ['ASIGNADO', 'REASIGNADO'].includes(sol.estado)
    },

    async tomarSolicitudDesdeMenu() {
      if (!this.solicitudMenuContextual) return
      const sol = this.solicitudMenuContextual
      const index = this.solicitudesOrdenadas.findIndex(s => s.id === sol.id)
      this.seleccionarSolicitud(sol, index)
      await this.tomarSolicitudActual()
    },

    reasignarSolicitudDesdeMenu() {
      if (!this.solicitudMenuContextual) return
      const sol = this.solicitudMenuContextual
      const index = this.solicitudesOrdenadas.findIndex(s => s.id === sol.id)
      this.seleccionarSolicitud(sol, index)
      this.abrirDialogReasignar()
    },

    async finalizarSolicitudDesdeMenu() {
      if (!this.solicitudMenuContextual) return
      const sol = this.solicitudMenuContextual
      const index = this.solicitudesOrdenadas.findIndex(s => s.id === sol.id)
      this.seleccionarSolicitud(sol, index)
      this.dialogFinalizar = true
    },

    contactarWhatsApp() {
      if (!this.solicitudMenuContextual) return
      const sol = this.solicitudMenuContextual
      if (!sol.telefono) return
      const tel = String(sol.telefono).replace(/^549/, '')
      const nombre = sol.revendedor_nombre || 'Revendedor'
      const msg = encodeURIComponent(`Hola ${nombre}, soy asesor de Dycar. Vi tu solicitud y quería comunicarme con vos.`)
      window.open(`https://wa.me/549${tel}?text=${msg}`, '_blank')
    },

    filaSolicitudClass(sol) {
      if (sol.estado === 'REASIGNADO') return 'revendedores-solicitud--reasignado'
      if (sol.estado === 'ASIGNADO') return 'revendedores-solicitud--asignado'
      return ''
    },

    colorEstado(estado) {
      switch (estado) {
        case 'PENDIENTE': return 'warning'
        case 'ASIGNADO': return 'info'
        case 'REASIGNADO': return 'purple'
        case 'RESUELTO': return 'success'
        default: return 'secondary'
      }
    },

    truncar(texto, max) {
      if (!texto) return ''
      return texto.length > max ? texto.substring(0, max) + '...' : texto
    },

    fechaRelativa(fecha) {
      if (!fecha) return ''
      // El backend envía la fecha con sufijo Z (UTC) pero en realidad es hora
      // local del servidor SQL (GETDATE(), no GETUTCDATE()). Removemos la Z
      // para parsearla como hora local del navegador y comparar correctamente.
      const fechaLocal = String(fecha).replace(/Z$/, '')
      const f = moment(fechaLocal)
      const ahora = moment()
      const diffMin = ahora.diff(f, 'minutes')
      if (diffMin < 1) return 'Ahora'
      if (diffMin < 60) return `Hace ${diffMin}m`
      const diffHrs = Math.floor(diffMin / 60)
      if (diffHrs < 24) return `Hace ${diffHrs}h`
      const diffDias = Math.floor(diffHrs / 24)
      if (diffDias === 1) return 'Ayer'
      if (diffDias < 7) return `Hace ${diffDias} días`
      return f.format('DD/MM/YYYY')
    },

    formatearFechaHora(fecha) {
      if (!fecha) return ''
      // Misma lógica que fechaRelativa: el backend envía Z pero es hora local.
      const fechaLocal = String(fecha).replace(/Z$/, '')
      return moment(fechaLocal).format('DD/MM/YYYY HH:mm')
    },

    async tomarSolicitudActual() {
      if (!this.solicitudSeleccionada) return
      this.tomandoSolicitud = true
      const solicitudTomada = await this.$store.dispatch('revendedores/tomar_solicitud', this.solicitudSeleccionada.id)

      this.tomandoSolicitud = false

      if (!solicitudTomada) return

      this.$store.dispatch('show_snackbar', {
        text: 'Solicitud asignada correctamente',
        color: 'success'
      })
      await this.recargarSolicitudes()
    },

    async finalizarSolicitudActual() {
      if (!this.solicitudSeleccionada) return

      this.finalizandoSolicitud = true
      const solicitudFinalizada = await this.$store.dispatch('revendedores/update_solicitud', {
        id: this.solicitudSeleccionada.id,
        payload: { estado: 'RESUELTO' }
      })
      this.finalizandoSolicitud = false

      if (!solicitudFinalizada) return

      this.$store.dispatch('show_snackbar', {
        text: 'Solicitud finalizada correctamente',
        color: 'success'
      })
      await this.recargarSolicitudes()
    },

    async confirmarFinalizar() {
      await this.finalizarSolicitudActual()
      this.dialogFinalizar = false
    },

    abrirDialogReasignar() {
      if (!this.solicitudSeleccionada) return
      this.reasignarForm = {
        asesor_asignado_id: null
      }
      this.dialogReasignar = true
    },

    async confirmarReasignacion() {
      if (!this.solicitudSeleccionada) return
      if (!this.reasignarForm.asesor_asignado_id) {
        this.$store.dispatch('show_snackbar', {
          text: 'Seleccione un asesor para reasignar la solicitud',
          color: 'warning'
        })
        return
      }

      this.reasignandoSolicitud = true
      const solicitudReasignada = await this.$store.dispatch('revendedores/reasignar_solicitud', {
        id: this.solicitudSeleccionada.id,
        asesor_asignado_id: this.reasignarForm.asesor_asignado_id
      })
      this.reasignandoSolicitud = false

      if (!solicitudReasignada) return

      this.dialogReasignar = false
      this.$store.dispatch('show_snackbar', {
        text: 'Solicitud reasignada correctamente',
        color: 'success'
      })
      await this.recargarSolicitudes()
    },

    // ── Inicialización desde query param ──────────────────────────

    async initDesdeQuery() {
      const solicitudId = this.$route.query.solicitud_id
      if (!solicitudId) return

      const id = parseInt(solicitudId, 10)
      if (!id) return

      // Cargar solicitudes primero
      await this.$store.dispatch('revendedores/fetch_solicitudes')

      // Buscar la solicitud por ID en la lista cargada
      const index = this.solicitudesOrdenadas.findIndex(s => s.id === id)
      if (index !== -1) {
        this.seleccionarSolicitud(this.solicitudesOrdenadas[index], index)

        // Si la solicitud está PENDIENTE, tomarla automáticamente
        if (this.solicitudesOrdenadas[index].estado === 'PENDIENTE') {
          const solicitudTomada = await this.$store.dispatch('revendedores/tomar_solicitud', id)

          if (solicitudTomada) {
            this.$store.dispatch('show_snackbar', {
              text: 'Solicitud asignada automáticamente',
              color: 'info'
            })
            await this.recargarSolicitudes()
          }
        }
      } else {
        this.$store.dispatch('show_snackbar', {
          text: 'La solicitud referenciada no se encontró o no pertenece a este sector',
          color: 'warning'
        })
      }
    }
  },

  async mounted() {
    await this.$store.dispatch('revendedores/fetch_permisos')
    if (this.puedeGestionarAbm) {
      this.buscar()
      this.$store.dispatch('revendedores/fetch_sai_vendedores')
    }
    await this.cargarUsuariosSolicitudes()
    await this.cargarRevendedores()

    if (this.$route.query.solicitud_id) {
      await this.initDesdeQuery()
      return
    }

    await this.recargarSolicitudes()

    // Polling automático: busca nuevas solicitudes cada 5 minutos
    this.pollingInterval = setInterval(() => {
      if (this.loadingSolicitudes) return
      this.recargarSolicitudes()
    }, 5 * 60 * 1000) // 5 minutos
  },

  beforeDestroy() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval)
      this.pollingInterval = null
    }
  }
}
</script>

<style scoped>
.revendedores-mobile-divider {
  border-color: var(--v-tab-base) !important;
}

@media (min-width: 1264px) {
  .revendedores-col--left {
    border-right: 1px solid var(--v-tab-base);
    padding-right: 10px !important;
  }

  .revendedores-col--right {
    padding-left: 10px !important;
  }
}

.revendedores-muted-text {
  color: var(--v-swalText-base) !important;
  opacity: 0.72;
}

.revendedores-solicitud--asignado {
  background-color: var(--v-rowsel-base) !important;
  background-color: color-mix(in srgb, var(--v-rowsel-base) 72%, var(--v-swalBkg-base)) !important;
}

.revendedores-solicitud--reasignado {
  background-color: var(--v-secondary-base) !important;
  background-color: color-mix(in srgb, var(--v-secondary-base) 78%, var(--v-swalBkg-base)) !important;
}

.revendedores-solicitud {
  padding-top: 2px;
  padding-bottom: 2px;
}

.revendedores-solicitud .v-list-item__title,
.revendedores-solicitud .v-list-item__subtitle {
  color: var(--v-swalText-base) !important;
  opacity: 1 !important;
}

.revendedores-solicitud .revendedores-muted-text {
  opacity: 0.84 !important;
}

.revendedores-table-wrap {
  overflow-x: auto;
}

.revendedores-add-btn {
  transform: translateY(-4px);
}

@media (max-width: 1263px) {
  .revendedores-section-header {
    gap: 12px;
  }
}

@media (max-width: 600px) {
  .revendedores-section-header {
    align-items: flex-start !important;
    flex-direction: column;
  }

  .revendedores-table-wrap {
    margin: 0 -8px;
  }
}
</style>
